// Per-student granular tracker for teachers / center-admins / co-admins.
// Three tabs, tightly scoped so nothing bleeds together:
//   Overview  — headline counts + per-section accuracy (R/L) + last activity
//   Practice  — writing & speaking PRACTICE responses (mock_form == null),
//               grouped by section. Reading/Listening practice shows up as
//               per-task-type accuracy rows.
//   Full Mocks — one card per (exam, form, day) sitting on a sealed form
//                (mock_form >= 23). Each card lists the four sections with
//                their score / response count and expands to show every
//                writing / speaking response inline with audio + AI feedback.
import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, AudioLines, BookOpen, ChevronDown, ClipboardList, FileText, Headphones,
  Loader2, Mic2, Play, TrendingUp,
} from 'lucide-react';

import { api } from '@/lib/api';
import AppShell from '@/components/AppShell';
import { cn } from '@/lib/utils';
import { prettyTask } from '@/lib/labels';

const EXAM_LABEL = { celpip: 'CELPIP', pte: 'PTE Core' };

const STATUS_META = {
  scored:      { label: 'Scored',    className: 'border-emerald-200 bg-emerald-50 text-emerald-700' },
  queued:      { label: 'Queued',    className: 'border-amber-200 bg-amber-50 text-amber-700' },
  pending:     { label: 'Pending',   className: 'border-slate-200 bg-slate-50 text-slate-600' },
  gate_zeroed: { label: 'Gate zero', className: 'border-rose-200 bg-rose-50 text-rose-700' },
};

const SECTION_META = {
  reading:   { label: 'Reading',   icon: BookOpen,   tone: 'bg-violet-50 text-violet-700' },
  listening: { label: 'Listening', icon: Headphones, tone: 'bg-sky-50 text-sky-700' },
  writing:   { label: 'Writing',   icon: FileText,   tone: 'bg-indigo-50 text-indigo-700' },
  speaking:  { label: 'Speaking',  icon: Mic2,       tone: 'bg-emerald-50 text-emerald-700' },
};

const fmtDate = (iso) => (!iso ? '' : new Date(iso).toLocaleString(undefined, {
  year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
}));
const fmtDay  = (iso) => (!iso ? '' : new Date(iso).toLocaleDateString(undefined, {
  year: 'numeric', month: 'short', day: 'numeric',
}));

function StatusChip({ status }) {
  const meta = STATUS_META[status] || STATUS_META.pending;
  return (
    <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold', meta.className)}>
      {meta.label}
    </span>
  );
}

function AudioPlayer({ userId, submissionId }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, [url]);
  const load = async () => {
    setLoading(true); setErr('');
    try { setUrl(await api.orgStudentAudioBlob(userId, submissionId)); }
    catch (e) { setErr(e.response?.data?.detail || 'Could not load audio'); }
    finally { setLoading(false); }
  };
  if (url) return <audio controls src={url} data-testid={`audio-${submissionId}`} className="mt-2 w-full max-w-md" />;
  return (
    <div className="mt-2">
      <button
        type="button" onClick={load} disabled={loading}
        className="inline-flex items-center gap-1.5 rounded-md border border-indigo-300 bg-white px-3 py-1.5 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 disabled:opacity-50"
        data-testid={`audio-load-${submissionId}`}
      >
        {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Play className="h-3.5 w-3.5" />}
        {loading ? 'Loading…' : 'Play recording'}
      </button>
      {err && <p className="mt-1 text-xs text-rose-600">{err}</p>}
    </div>
  );
}

function FeedbackBlock({ feedback }) {
  if (!feedback) return null;
  const bands = Object.entries(feedback).filter(([k, v]) => typeof v === 'number' && k !== 'raw');
  const comments     = feedback.comments || feedback.summary || feedback.notes || null;
  const strengths    = Array.isArray(feedback.strengths) ? feedback.strengths : null;
  const improvements = Array.isArray(feedback.improvements || feedback.suggestions)
    ? (feedback.improvements || feedback.suggestions) : null;
  const hasKnown = bands.length > 0 || comments || strengths || improvements;
  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/50 p-3 text-sm">
      <p className="mono-stat !text-slate-500">Feedback</p>
      {bands.length > 0 && (
        <div className="mt-2 grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
          {bands.map(([k, v]) => (
            <div key={k} className="rounded border border-slate-200 bg-white px-2.5 py-1.5 text-center">
              <p className="h-display text-lg font-bold tabular-nums text-slate-900">{v}</p>
              <p className="mono-stat !text-slate-500">{k.replace(/_/g, ' ')}</p>
            </div>
          ))}
        </div>
      )}
      {comments && (
        <div className="mt-3">
          <p className="text-xs font-semibold text-slate-700">Comments</p>
          <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">
            {typeof comments === 'string' ? comments : JSON.stringify(comments)}
          </p>
        </div>
      )}
      {strengths && strengths.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-semibold text-emerald-700">Strengths</p>
          <ul className="mt-1 list-disc pl-5 text-sm text-slate-700">{strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
        </div>
      )}
      {improvements && improvements.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-semibold text-indigo-700">To improve</p>
          <ul className="mt-1 list-disc pl-5 text-sm text-slate-700">{improvements.map((s, i) => <li key={i}>{s}</li>)}</ul>
        </div>
      )}
      {!hasKnown && (
        <pre className="mt-2 max-h-64 overflow-auto rounded border border-slate-200 bg-white p-3 text-[11px] leading-relaxed text-slate-700">
          {JSON.stringify(feedback, null, 2)}
        </pre>
      )}
    </div>
  );
}

// One writing or speaking response — reused across Practice and Full Mocks tabs.
function ResponseCard({ s, userId }) {
  const [expanded, setExpanded] = useState(false);
  const isSpeaking = s.section === 'speaking';
  return (
    <li
      className={cn('rounded-xl border bg-white transition-colors', expanded ? 'border-indigo-300 shadow-sm' : 'border-slate-200')}
      data-testid={`submission-${s.submission_id}`}
    >
      <button
        type="button" onClick={() => setExpanded((v) => !v)}
        className="flex w-full flex-wrap items-center gap-3 px-4 py-3.5 text-left"
        data-testid={`submission-toggle-${s.submission_id}`}
      >
        <span className={cn('grid h-9 w-9 shrink-0 place-items-center rounded-lg', isSpeaking ? 'bg-emerald-50 text-emerald-700' : 'bg-indigo-50 text-indigo-700')}>
          {isSpeaking ? <AudioLines className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="truncate font-display text-[15px] font-semibold text-slate-900">{s.prompt_title}</p>
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
              {EXAM_LABEL[s.exam] || s.exam?.toUpperCase()}
            </span>
            <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
              {prettyTask(s.task_type) || s.task_type}
            </span>
          </div>
          <p className="mt-0.5 text-xs text-slate-500">
            Submitted {fmtDate(s.created_at)}
            {typeof s.word_count === 'number' ? ` · ${s.word_count} words` : ''}
            {typeof s.duration_sec === 'number' ? ` · ${Math.round(s.duration_sec)}s` : ''}
          </p>
        </div>
        <StatusChip status={s.status} />
        <ChevronDown className={cn('h-4 w-4 text-slate-400 transition-transform', expanded && 'rotate-180')} />
      </button>
      {expanded && (
        <div className="border-t border-slate-100 px-4 py-4">
          {isSpeaking ? (
            <div>
              <p className="mono-stat !text-slate-500">Recording</p>
              <AudioPlayer userId={userId} submissionId={s.submission_id} />
              {s.transcript && (
                <div className="mt-3">
                  <p className="mono-stat !text-slate-500">Transcript</p>
                  <p className="mt-1 whitespace-pre-wrap rounded-md border border-slate-200 bg-slate-50/50 p-3 text-sm text-slate-800" data-testid={`transcript-${s.submission_id}`}>{s.transcript}</p>
                </div>
              )}
            </div>
          ) : (
            <div>
              <p className="mono-stat !text-slate-500">Response</p>
              <p className="mt-1 whitespace-pre-wrap rounded-md border border-slate-200 bg-slate-50/50 p-3 text-sm text-slate-800" data-testid={`response-text-${s.submission_id}`}>
                {s.text || <span className="italic text-slate-400">(no text captured)</span>}
              </p>
            </div>
          )}
          {s.gate_flags?.length > 0 && (
            <p className="mt-3 text-xs text-rose-700"><strong>Gate flags:</strong> {s.gate_flags.join(', ')}</p>
          )}
          <FeedbackBlock feedback={s.feedback} />
          {s.queue_reason && (
            <p className="mt-3 text-xs text-amber-700">Queued — <span className="font-mono">{s.queue_reason}</span></p>
          )}
        </div>
      )}
    </li>
  );
}

// A single R/L accuracy row for the Practice tab (grouped by task type)
function AccuracyRow({ row }) {
  const meta = SECTION_META[row.section] || SECTION_META.reading;
  const Icon = meta.icon;
  const pct = row.accuracy == null ? null : Math.round(row.accuracy * 100);
  return (
    <li className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3" data-testid={`practice-rl-${row.exam}-${row.section}-${row.task_type}`}>
      <span className={cn('grid h-9 w-9 shrink-0 place-items-center rounded-lg', meta.tone)}>
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-display text-[14px] font-semibold text-slate-900">{prettyTask(row.task_type) || row.task_type}</p>
        <p className="mt-0.5 text-xs text-slate-500">
          {EXAM_LABEL[row.exam] || row.exam?.toUpperCase()} · {meta.label} · {row.attempts} attempt{row.attempts === 1 ? '' : 's'}
          {row.last_at ? ` · last ${fmtDay(row.last_at)}` : ''}
        </p>
      </div>
      <div className="text-right">
        <p className="h-display text-lg font-extrabold tabular-nums text-slate-900">
          {pct == null ? '—' : `${pct}%`}
        </p>
        <p className="mono-stat !text-slate-500">{row.correct}/{row.total}</p>
      </div>
    </li>
  );
}

// One full-mock sitting card. Header row shows all four section pills; expand
// to see each writing / speaking response inline.
function MockSittingCard({ sitting, userId }) {
  const [expanded, setExpanded] = useState(false);
  const { sections, summary } = sitting;
  return (
    <li
      className={cn('overflow-hidden rounded-xl border bg-white', expanded ? 'border-indigo-300 shadow-sm' : 'border-slate-200')}
      data-testid={`sitting-${sitting.sitting_id}`}
    >
      <button
        type="button" onClick={() => setExpanded((v) => !v)}
        className="w-full text-left"
        data-testid={`sitting-toggle-${sitting.sitting_id}`}
      >
        <div className="flex flex-wrap items-center gap-3 px-4 py-3.5">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-display text-[15px] font-semibold text-slate-900">
                {EXAM_LABEL[sitting.exam] || sitting.exam?.toUpperCase()} — Full Mock #{sitting.form}
              </p>
              <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
                {summary.sections_completed}/4 sections
              </span>
            </div>
            <p className="mt-0.5 text-xs text-slate-500">
              Sat {fmtDate(sitting.at)}
              {summary.auto_total > 0 ? ` · R+L: ${summary.auto_correct}/${summary.auto_total}` : ''}
              {` · ${summary.writing_count} writing`}
              {` · ${summary.speaking_count} speaking`}
            </p>
          </div>
          <ChevronDown className={cn('h-4 w-4 text-slate-400 transition-transform', expanded && 'rotate-180')} />
        </div>
        <div className="grid grid-cols-2 gap-2 border-t border-slate-100 bg-slate-50/40 px-4 py-3 sm:grid-cols-4" data-testid={`sitting-sections-${sitting.sitting_id}`}>
          {['reading', 'listening', 'writing', 'speaking'].map((k) => {
            const meta = SECTION_META[k];
            const Icon = meta.icon;
            const val = sections[k];
            let display;
            if (k === 'reading' || k === 'listening') {
              display = val ? `${val.n_correct}/${val.n_total}` : '—';
            } else {
              display = val && val.length ? `${val.length} response${val.length === 1 ? '' : 's'}` : '—';
            }
            const missing = (k === 'reading' || k === 'listening') ? !val : !(val && val.length);
            return (
              <div key={k} className={cn('rounded-lg border px-3 py-2', missing ? 'border-dashed border-slate-200 bg-white/50 text-slate-400' : 'border-slate-200 bg-white text-slate-800')} data-testid={`sitting-${sitting.sitting_id}-${k}`}>
                <div className="flex items-center gap-1.5">
                  <span className={cn('grid h-6 w-6 place-items-center rounded', meta.tone)}>
                    <Icon className="h-3 w-3" />
                  </span>
                  <span className="mono-stat !text-slate-500">{meta.label}</span>
                </div>
                <p className="mt-1 h-display text-[15px] font-bold tabular-nums">{display}</p>
              </div>
            );
          })}
        </div>
      </button>
      {expanded && (
        <div className="border-t border-slate-100 bg-white px-4 py-4">
          {['writing', 'speaking'].map((k) => {
            const list = sections[k] || [];
            if (list.length === 0) return null;
            const meta = SECTION_META[k];
            return (
              <div key={k} className="mb-4 last:mb-0" data-testid={`sitting-${sitting.sitting_id}-${k}-list`}>
                <div className="mb-2 flex items-center gap-2">
                  <span className={cn('grid h-6 w-6 place-items-center rounded', meta.tone)}>
                    <meta.icon className="h-3 w-3" />
                  </span>
                  <p className="mono-stat !text-slate-500">{meta.label} responses</p>
                </div>
                <ul className="space-y-2">
                  {list.map((s) => (
                    <ResponseCard key={s.submission_id} s={s} userId={userId} />
                  ))}
                </ul>
              </div>
            );
          })}
          {!sections.writing.length && !sections.speaking.length && (
            <p className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500">
              No writing or speaking responses recorded for this sitting.
            </p>
          )}
        </div>
      )}
    </li>
  );
}

export default function StudentProgressPage() {
  const { user_id } = useParams();
  const [subData, setSubData] = useState(null);
  const [mockData, setMockData] = useState(null);
  const [student, setStudent] = useState(null);
  const [err, setErr] = useState('');
  const [tab, setTab] = useState('overview'); // overview | practice | mocks

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [sub, stu, mocks] = await Promise.all([
          api.orgStudentSubmissions(user_id),
          api.orgStudent(user_id),
          api.orgStudentMockSittings(user_id),
        ]);
        if (!mounted) return;
        setSubData(sub.data);
        setMockData(mocks.data);
        setStudent(stu.data.student);
      } catch (e) {
        setErr(e.response?.data?.detail || 'Could not load progress.');
      }
    })();
    return () => { mounted = false; };
  }, [user_id]);

  // Practice submissions = anything not tagged to a sealed mock form
  const practiceSubs = useMemo(() => {
    if (!subData) return { writing: [], speaking: [] };
    const out = { writing: [], speaking: [] };
    for (const s of subData.submissions) {
      if (s.mock_form) continue;
      if (s.section === 'writing') out.writing.push(s);
      else if (s.section === 'speaking') out.speaking.push(s);
    }
    return out;
  }, [subData]);

  const practiceRL = useMemo(() => {
    if (!subData?.practice_rl) return { reading: [], listening: [] };
    const g = { reading: [], listening: [] };
    for (const r of subData.practice_rl) (g[r.section] || []).push(r);
    return g;
  }, [subData]);

  if (err) return <AppShell><p className="text-sm text-rose-600" data-testid="progress-error">{err}</p></AppShell>;
  if (!subData || !mockData) {
    return (
      <AppShell>
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading progress…
        </div>
      </AppShell>
    );
  }

  const mockCounts    = mockData.counts;
  const practiceTotal = practiceSubs.writing.length + practiceSubs.speaking.length;
  const totalRL       = subData.practice_rl.reduce((a, b) => a + b.attempts, 0);

  const tabs = [
    { id: 'overview', label: 'Overview',        icon: TrendingUp,    count: null },
    { id: 'practice', label: 'Practice',        icon: ClipboardList, count: practiceTotal + totalRL },
    { id: 'mocks',    label: 'Full Mock History', icon: FileText,    count: mockCounts.total_sittings },
  ];

  return (
    <AppShell>
      <div className="pb-16" data-testid="progress-page">
        <Link to="/org" className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900" data-testid="progress-back">
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Teacher Admin
        </Link>

        <header className="mt-3 border-b border-slate-200 pb-6">
          <p className="mono-stat">Student progress</p>
          <h1 className="mt-1 h-display text-3xl font-extrabold text-slate-900" data-testid="progress-student-name">{student?.name}</h1>
          <p className="text-sm text-slate-500">{student?.email}</p>

          <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5" data-testid="progress-counts">
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <p className="h-display text-2xl font-extrabold tabular-nums text-slate-900" data-testid="count-mocks">{mockCounts.total_sittings}</p>
              <p className="mono-stat !text-slate-500">Full mock sittings</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <p className="h-display text-2xl font-extrabold tabular-nums text-slate-900" data-testid="count-mocks-complete">{mockCounts.complete_sittings}</p>
              <p className="mono-stat !text-slate-500">Complete (all 4 sections)</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <p className="h-display text-2xl font-extrabold tabular-nums text-slate-900" data-testid="count-writing">{practiceSubs.writing.length}</p>
              <p className="mono-stat !text-slate-500">Practice writing</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <p className="h-display text-2xl font-extrabold tabular-nums text-slate-900" data-testid="count-speaking">{practiceSubs.speaking.length}</p>
              <p className="mono-stat !text-slate-500">Practice speaking</p>
            </div>
            <div className="rounded-lg border border-slate-200 bg-white p-3">
              <p className="h-display text-2xl font-extrabold tabular-nums text-slate-900" data-testid="count-rl-attempts">{totalRL}</p>
              <p className="mono-stat !text-slate-500">R/L practice attempts</p>
            </div>
          </div>
        </header>

        <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="progress-tabs">
          {tabs.map((t) => (
            <button
              key={t.id} type="button" onClick={() => setTab(t.id)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors',
                tab === t.id ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-400',
              )}
              data-testid={`progress-tab-${t.id}`}
            >
              <t.icon className="h-3.5 w-3.5" />
              {t.label}{t.count == null ? '' : ` (${t.count})`}
            </button>
          ))}
        </div>

        {tab === 'overview' && (
          <div className="mt-6 space-y-6" data-testid="tab-overview">
            <section>
              <h2 className="mono-stat">Reading & Listening — practice accuracy</h2>
              {subData.practice_rl.length === 0 ? (
                <p className="mt-2 rounded-lg border border-dashed border-slate-200 bg-white/50 p-8 text-center text-sm text-slate-500" data-testid="overview-rl-empty">
                  No reading or listening practice attempts yet.
                </p>
              ) : (
                <ul className="mt-2 space-y-2">
                  {subData.practice_rl.map((r) => (
                    <AccuracyRow key={`${r.exam}-${r.section}-${r.task_type}`} row={r} />
                  ))}
                </ul>
              )}
            </section>
            <section>
              <h2 className="mono-stat">Writing & Speaking — practice</h2>
              <div className="mt-2 grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <p className="h-display text-2xl font-extrabold tabular-nums">{practiceSubs.writing.length}</p>
                  <p className="mono-stat !text-slate-500">Writing responses</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <p className="h-display text-2xl font-extrabold tabular-nums">{practiceSubs.speaking.length}</p>
                  <p className="mono-stat !text-slate-500">Speaking responses</p>
                </div>
              </div>
            </section>
            <section>
              <h2 className="mono-stat">Full mocks</h2>
              <div className="mt-2 grid grid-cols-2 gap-3">
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <p className="h-display text-2xl font-extrabold tabular-nums">{mockCounts.total_sittings}</p>
                  <p className="mono-stat !text-slate-500">Sittings started</p>
                </div>
                <div className="rounded-lg border border-slate-200 bg-white p-4">
                  <p className="h-display text-2xl font-extrabold tabular-nums">{mockCounts.complete_sittings}</p>
                  <p className="mono-stat !text-slate-500">Complete sittings</p>
                </div>
              </div>
            </section>
          </div>
        )}

        {tab === 'practice' && (
          <div className="mt-6 space-y-6" data-testid="tab-practice">
            <section>
              <div className="mb-2 flex items-center gap-2">
                <span className={cn('grid h-6 w-6 place-items-center rounded', SECTION_META.reading.tone)}>
                  <BookOpen className="h-3 w-3" />
                </span>
                <h2 className="mono-stat">Reading — task accuracy</h2>
              </div>
              {practiceRL.reading.length === 0
                ? <p className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500" data-testid="practice-reading-empty">No reading practice yet.</p>
                : <ul className="space-y-2">{practiceRL.reading.map((r) => <AccuracyRow key={`${r.exam}-${r.task_type}`} row={r} />)}</ul>
              }
            </section>
            <section>
              <div className="mb-2 flex items-center gap-2">
                <span className={cn('grid h-6 w-6 place-items-center rounded', SECTION_META.listening.tone)}>
                  <Headphones className="h-3 w-3" />
                </span>
                <h2 className="mono-stat">Listening — task accuracy</h2>
              </div>
              {practiceRL.listening.length === 0
                ? <p className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500" data-testid="practice-listening-empty">No listening practice yet.</p>
                : <ul className="space-y-2">{practiceRL.listening.map((r) => <AccuracyRow key={`${r.exam}-${r.task_type}`} row={r} />)}</ul>
              }
            </section>
            <section>
              <div className="mb-2 flex items-center gap-2">
                <span className={cn('grid h-6 w-6 place-items-center rounded', SECTION_META.writing.tone)}>
                  <FileText className="h-3 w-3" />
                </span>
                <h2 className="mono-stat">Writing responses ({practiceSubs.writing.length})</h2>
              </div>
              {practiceSubs.writing.length === 0
                ? <p className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500" data-testid="practice-writing-empty">No practice writing responses yet.</p>
                : <ul className="space-y-2">{practiceSubs.writing.map((s) => <ResponseCard key={s.submission_id} s={s} userId={user_id} />)}</ul>
              }
            </section>
            <section>
              <div className="mb-2 flex items-center gap-2">
                <span className={cn('grid h-6 w-6 place-items-center rounded', SECTION_META.speaking.tone)}>
                  <Mic2 className="h-3 w-3" />
                </span>
                <h2 className="mono-stat">Speaking responses ({practiceSubs.speaking.length})</h2>
              </div>
              {practiceSubs.speaking.length === 0
                ? <p className="rounded-lg border border-dashed border-slate-200 p-6 text-center text-sm text-slate-500" data-testid="practice-speaking-empty">No practice speaking responses yet.</p>
                : <ul className="space-y-2">{practiceSubs.speaking.map((s) => <ResponseCard key={s.submission_id} s={s} userId={user_id} />)}</ul>
              }
            </section>
          </div>
        )}

        {tab === 'mocks' && (
          <div className="mt-6" data-testid="tab-mocks">
            {mockData.sittings.length === 0 ? (
              <p className="rounded-lg border border-dashed border-slate-200 bg-white/50 p-10 text-center text-sm text-slate-500" data-testid="mocks-empty">
                This student hasn&apos;t sat a full mock yet. Full mocks appear here once they submit at least one section of a sealed form.
              </p>
            ) : (
              <ul className="space-y-3">
                {mockData.sittings.map((s) => <MockSittingCard key={s.sitting_id} sitting={s} userId={user_id} />)}
              </ul>
            )}
          </div>
        )}
      </div>
    </AppShell>
  );
}
